/**
 * Vincent #17 batch FLHQ (Eric 2026-02-06) — Page Corbeille de la Base de Connaissances.
 *
 * Liste tous les KU avec status="deleted" (soft delete) pour l'org courante.
 * - Bouton "Restaurer" → POST /api/agent/knowledge-units/{id}/restore (rebascule
 *   vers le previous_status, généralement pending_review ou approved).
 * - Bouton "Supprimer définitivement" → DELETE .../permanent (irréversible,
 *   demande confirmation typée "EFFACER").
 *
 * Accès : admin / founder / concepteur / employee_advanced uniquement.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Trash, ArrowCounterClockwise, SpinnerGap } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent } from "../utils/permissions";
import { API_BASE as API } from "../lib/apiBase";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);

export default function KnowledgeTrashPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmHard, setConfirmHard] = useState(null); // {id, title, typed}

  // Eric 2026-06-25 (iter117 B2) — migré vers le helper canonique canEditContent
  // (whitelist : founder, admin, ceo, president, vp, concepteur, employee_advanced,
  // access_level=avance). Aligné avec backend require_editor de core/auth.py.
  const canManage = canEditContent(user);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/agent/knowledge-units/trash`, { withCredentials: true });
      setItems(data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Erreur lors du chargement de la corbeille", "Failed to load trash"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) {
      load();
    }
  }, [canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async (item) => {
    try {
      await axios.post(`${API}/api/agent/knowledge-units/${item.id}/restore`, {}, { withCredentials: true });
      toast.success(L(lang, "Connaissance restaurée", "Knowledge restored"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Erreur restauration", "Restore failed"));
    }
  };

  const confirmHardDelete = async () => {
    if (!confirmHard || confirmHard.typed.trim().toUpperCase() !== "EFFACER") return;
    try {
      await axios.delete(`${API}/api/agent/knowledge-units/${confirmHard.id}/permanent`, { withCredentials: true });
      toast.success(L(lang, "Connaissance effacée définitivement", "Knowledge permanently deleted"));
      setConfirmHard(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L(lang, "Erreur suppression définitive", "Permanent delete failed"));
    }
  };

  if (!canManage) {
    return (
      <div className="p-8 text-center text-zinc-600">
        {L(lang, "Accès réservé aux rôles admin / founder / concepteur / employé avancé.", "Access restricted to admin / founder / designer / advanced employee.")}
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="knowledge-trash-page">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-600"
          data-testid="trash-back-btn"
          aria-label={L(lang, "Retour", "Back")}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">
            🗑 {L(lang, "Corbeille — Base de connaissances", "Trash — Knowledge base")}
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            {L(lang,
              `${items.length} connaissance(s) effacée(s). Restaurable ou supprimable définitivement.`,
              `${items.length} deleted knowledge unit(s). Restorable or permanently deletable.`
            )}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <SpinnerGap size={32} className="animate-spin text-[#002FA7]" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-zinc-400" data-testid="trash-empty">
          <Trash size={48} className="mx-auto mb-3 opacity-30" />
          <p>{L(lang, "La corbeille est vide.", "Trash is empty.")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-zinc-200 rounded-lg p-4 flex items-start justify-between gap-4"
              data-testid={`trash-item-${item.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 text-xs font-medium rounded bg-zinc-100 text-zinc-700">
                    {item.type === "qa" ? "Q&R"
                      : item.type === "procedure" ? "Procédure"
                      : item.type === "script" ? "Script"
                      : item.type === "concept" ? "Concept"
                      : item.type === "objection" ? "Objection"
                      : item.type}
                  </span>
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-rose-100 text-rose-800 border border-rose-200">
                    {L(lang, "EFFACÉE", "DELETED")}
                  </span>
                  {item.previous_status && (
                    <span className="text-[10px] text-zinc-500" title={L(lang, "Avant l'effacement", "Before deletion")}>
                      {L(lang, "ancien statut :", "previous status:")} <b>{item.previous_status}</b>
                    </span>
                  )}
                </div>
                <h3 className="font-medium text-sm leading-snug truncate">{item.title || L(lang, "Sans titre", "Untitled")}</h3>
                <div className="text-xs text-zinc-500 mt-1">
                  {item.deleted_at && (
                    <span>
                      {L(lang, "Effacée le", "Deleted on")} {new Date(item.deleted_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "short", timeStyle: "short" })}
                      {item.deleted_by ? ` ${L(lang, "par", "by")} ${item.deleted_by}` : ""}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => restore(item)}
                  className="px-3 py-1.5 rounded-lg border border-teal-300 text-teal-700 hover:bg-teal-50 text-xs font-semibold inline-flex items-center gap-1"
                  data-testid={`trash-restore-${item.id}`}
                  title={L(lang, "Restaurer cette connaissance", "Restore this knowledge")}
                >
                  <ArrowCounterClockwise size={14} weight="bold" />
                  {L(lang, "Restaurer", "Restore")}
                </button>
                <button
                  onClick={() => setConfirmHard({ id: item.id, title: item.title, typed: "" })}
                  className="px-3 py-1.5 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 text-xs font-semibold inline-flex items-center gap-1"
                  data-testid={`trash-permanent-${item.id}`}
                  title={L(lang, "Suppression définitive — irréversible", "Permanent delete — irreversible")}
                >
                  <Trash size={14} weight="bold" />
                  {L(lang, "Effacer définitivement", "Permanent delete")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Vincent #17 — Modal confirmation typée pour suppression définitive */}
      {confirmHard && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmHard(null)} data-testid="trash-permanent-confirm-overlay">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-rose-200 bg-rose-50 rounded-t-xl">
              <h2 className="font-bold text-lg text-rose-800">⚠️ {L(lang, "Suppression définitive", "Permanent deletion")}</h2>
              <p className="text-xs text-rose-700 mt-1">
                {L(lang,
                  "Cette connaissance sera EFFACÉE pour de bon. Plus aucune récupération possible.",
                  "This knowledge will be PERMANENTLY removed. No more recovery possible.")}
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="bg-zinc-50 border border-zinc-200 rounded p-3 text-sm">
                <strong>{confirmHard.title || L(lang, "Sans titre", "Untitled")}</strong>
              </div>
              <label className="block text-xs font-semibold text-zinc-700">
                {L(lang, <>Tape <span className="font-mono text-rose-700">EFFACER</span> pour confirmer :</>, <>Type <span className="font-mono text-rose-700">EFFACER</span> to confirm:</>)}
              </label>
              <input
                type="text"
                autoFocus
                value={confirmHard.typed}
                onChange={(e) => setConfirmHard({ ...confirmHard, typed: e.target.value })}
                className="w-full px-3 py-2 border-2 border-rose-300 rounded font-mono uppercase text-sm focus:outline-none focus:border-rose-500"
                placeholder="EFFACER"
                data-testid="trash-permanent-confirm-input"
              />
            </div>
            <div className="px-5 py-3 border-t bg-zinc-50 rounded-b-xl flex justify-end gap-2">
              <button onClick={() => setConfirmHard(null)} className="px-4 py-2 text-sm rounded-lg border hover:bg-zinc-100" data-testid="trash-permanent-cancel">
                {L(lang, "Annuler", "Cancel")}
              </button>
              <button
                onClick={confirmHardDelete}
                disabled={confirmHard.typed.trim().toUpperCase() !== "EFFACER"}
                className="px-4 py-2 text-sm rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="trash-permanent-confirm-submit"
              >
                🗑 {L(lang, "Effacer pour de bon", "Erase forever")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
